import { useEffect, useRef } from 'react'
import { atom, useAtomValue, useSetAtom } from 'jotai'
import { subscribeToConversationMessages } from './supportRealtime'
import { supportApi } from './supportApi'
import type { SupportUnreadPreview } from './types'

/** Set once we learn the conversation id from getUnread (or from opening the chat). */
export const supportConversationIdAtom = atom<string | null>(null)
/** Unread agent-reply count shown on the floating button. */
export const supportUnreadAtom = atom<number>(0)
/**
 * ATL-1343: the newest unread message, for the in-app toast. Set from the same
 * `getUnread` response as the count above, so the toast and the badge always describe the
 * same conversation state.
 *
 * Three states, and the third is load-bearing: a preview, `null` (the server answered, nothing is
 * unread), and `undefined` (**the server has not answered yet**). `undefined` is the initial value
 * because the toast's baseline rule is "the first value the SERVER reports is never announced" —
 * and an atom that started at `null` let the hook's mount-time effect spend that baseline on the
 * default before any fetch resolved, so the first `getUnread` of a cold start toasted whatever it
 * found, including a three-day-old message. `useSupportMessageToast` ignores `undefined` entirely.
 */
export const supportLatestUnreadAtom = atom<SupportUnreadPreview | null | undefined>(undefined)
/**
 * The message id the toast has already accounted for. `undefined` = the server has not been
 * observed yet this session, which is a BASELINE and never a toast (see `useSupportMessageToast`).
 *
 * It is an atom rather than a ref on purpose: `FloatingButtonWithModal` unmounts on the
 * `AddCartItem` screen (AppStack.js), and a ref would reset there — silently swallowing a message
 * that arrived while the merchant was adding a cart item.
 */
export const supportToastSeenIdAtom = atom<string | null | undefined>(undefined)
/** True while the chat panel is on screen (suppresses the badge — the user is looking). */
export const supportChatOpenAtom = atom<boolean>(false)
/**
 * Set to true by the push-notification handler (AppStack) to request that
 * FloatingButtonWithModal open the native support chat. The component resets
 * it to false after acting on it.
 */
export const supportChatOpenRequestAtom = atom<boolean>(false)

/**
 * ATL-1343 — response ordering for `getUnread`.
 *
 * The endpoint is called from two places (the mount/panel-close poll and every realtime signal) and
 * the realtime path has no cancellation, so two responses can land out of order. For a COUNT that
 * only meant a badge that healed on the next signal. For the toast it is worse: an older response
 * overwriting a newer one moves `supportLatestUnreadAtom` BACK to a message the toast has already
 * announced, which reads as a change and announces it a SECOND time — and then announces the newer
 * message again when the next fetch restores it. Two agent replies a second apart are enough.
 *
 * Module scope, not a ref: `FloatingButtonWithModal` unmounts on every screen in
 * `screensWithoutFloatingButton`, and a per-mount counter would restart at 0 while a response from
 * the previous mount was still in flight.
 */
let unreadRequestSeq = 0
let unreadAppliedSeq = 0

/** Claim the sequence number for a `getUnread` call about to be issued. */
const nextUnreadSeq = (): number => ++unreadRequestSeq

/**
 * True when `seq`'s response is the newest one to have come back, and claims that position.
 * Call it only once, immediately before applying a response.
 */
const isFreshestUnread = (seq: number): boolean => {
  if (seq <= unreadAppliedSeq) return false
  unreadAppliedSeq = seq
  return true
}

/** Drop every in-flight response: they describe an account that is no longer signed in. */
const discardInFlightUnread = (): void => {
  unreadAppliedSeq = unreadRequestSeq
}

/**
 * The account the atoms above currently describe. Module scope for the same reason as the
 * sequence counters — the atoms outlive the component, so the identity they belong to must too.
 * `undefined` means "no account observed yet", which is not a switch.
 */
let observedAccountKey: string | undefined

/**
 * DR-2: app-level watcher. Polls getUnread on mount and whenever the panel closes
 * (self-heals missed realtime signals). While closed, also listens for realtime
 * signals and refetches the server count on each one — no local counting.
 * Mount once (e.g. in the always-rendered FloatingButtonWithModal).
 */
export const useSupportUnreadWatcher = (accountKey?: string | null): void => {
  const setConvId = useSetAtom(supportConversationIdAtom)
  const setUnread = useSetAtom(supportUnreadAtom)
  const setLatest = useSetAtom(supportLatestUnreadAtom)
  const setToastSeenId = useSetAtom(supportToastSeenIdAtom)
  const chatOpen = useAtomValue(supportChatOpenAtom)
  const chatOpenRef = useRef(chatOpen)
  useEffect(() => {
    chatOpenRef.current = chatOpen
  }, [chatOpen])

  // ATL-1343: an account switch invalidates every atom above, and the toast's baseline most of
  // all. These atoms are module state and nothing clears them on sign-out, so without this the
  // second merchant to sign in on a till starts with the first one's `supportToastSeenIdAtom`:
  // their first server answer is no longer a baseline, and a message that may be days old is
  // announced the moment the app finishes loading. Declared BEFORE the fetch effect so that in a
  // commit where both run, the reset lands first.
  useEffect(() => {
    const key = accountKey ?? undefined
    if (typeof observedAccountKey === 'string' && observedAccountKey !== key) {
      discardInFlightUnread()
      setConvId(null)
      setUnread(0)
      setLatest(undefined)
      setToastSeenId(undefined)
    }
    observedAccountKey = key
  }, [accountKey, setConvId, setUnread, setLatest, setToastSeenId])

  // initial load + re-poll whenever the panel closes (a freshly-created conversation
  // gets an id, and a missed realtime signal self-heals).
  useEffect(() => {
    if (chatOpen) return undefined
    let cancelled = false
    const seq = nextUnreadSeq()
    void (async () => {
      try {
        const { conversationId, unreadCount, latestMessage } = await supportApi.getUnread()
        // `cancelled` first: a response we are about to throw away must not claim the freshest
        // slot and silence the response that supersedes it.
        if (cancelled || !isFreshestUnread(seq)) return
        setConvId(conversationId)
        setUnread(unreadCount)
        setLatest(latestMessage)
      } catch {
        // best-effort; leave the badge unchanged
      }
    })()
    return () => {
      cancelled = true
    }
  }, [chatOpen, accountKey, setConvId, setUnread, setLatest])

  // live updates while closed: refetch the count on each realtime signal
  const conversationId = useAtomValue(supportConversationIdAtom)
  useEffect(() => {
    if (!conversationId) return undefined
    return subscribeToConversationMessages(
      conversationId,
      () => {
        if (chatOpenRef.current) return
        const seq = nextUnreadSeq()
        void supportApi
          .getUnread()
          .then(({ unreadCount, latestMessage }) => {
            if (!isFreshestUnread(seq)) return
            setUnread(unreadCount)
            setLatest(latestMessage)
          })
          .catch(() => {})
      },
      'unread',
    )
  }, [conversationId, setUnread, setLatest])
}
